import { useEffect, useState } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
} from 'recharts';
import * as logistaService from '@/services/logistaService';

const COLORS = ['#1B6CA8', '#D4AF37', '#2D7A2D', '#C0392B', '#9B59B6', '#E67E22'];

function MetricCard({ icon, label, value }) {
  return (
    <div className="rounded-2xl border border-edge bg-surface p-5 shadow-sm">
      <div className="text-2xl">{icon}</div>
      <p className="mt-2 text-2xl font-black text-fg">{value ?? '—'}</p>
      <p className="mt-0.5 text-sm font-semibold text-muted">{label}</p>
    </div>
  );
}

export default function LogistaAnalyticsPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    logistaService.getAnalytics()
      .then((r) => setData(r.data.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <div className="flex h-64 items-center justify-center"><div className="h-8 w-8 animate-spin rounded-full border-4 border-accent border-t-transparent" /></div>;
  }

  if (!data || data.total === 0) {
    return (
      <div className="space-y-5">
        <div>
          <h1 className="font-serif text-2xl font-black text-fg">Analytics</h1>
          <p className="text-sm text-muted">Desempenho da tua loja no jogo.</p>
        </div>
        <div className="rounded-2xl border-2 border-dashed border-edge bg-surface py-20 text-center">
          <p className="text-4xl">📊</p>
          <p className="mt-2 font-bold text-muted">Ainda sem dados</p>
          <p className="text-sm text-muted">Os dados aparecem assim que os primeiros jogadores resgatarem as tuas ofertas.</p>
        </div>
      </div>
    );
  }

  const monthGrowth = data.byMonth.length >= 2
    ? data.byMonth[data.byMonth.length - 1].count - data.byMonth[data.byMonth.length - 2].count
    : 0;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-serif text-2xl font-black text-fg">Analytics</h1>
        <p className="text-sm text-muted">Desempenho da tua loja no jogo.</p>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <MetricCard icon="🎫" label="Total resgates" value={data.total} />
        <MetricCard icon="📅" label="Este mês" value={data.thisMonth} />
        <MetricCard icon="⭐" label="Oferta top" value={data.topOffer?.title || '—'} />
        <MetricCard
          icon={monthGrowth >= 0 ? '📈' : '📉'}
          label="vs mês anterior"
          value={`${monthGrowth >= 0 ? '+' : ''}${monthGrowth}`}
        />
      </div>

      {/* Bar chart */}
      <div className="rounded-2xl border border-edge bg-surface p-5 shadow-sm">
        <h2 className="mb-4 font-bold text-fg">Resgates por mês</h2>
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={data.byMonth} margin={{ top: 0, right: 0, bottom: 0, left: -20 }}>
            <XAxis dataKey="label" tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
            <Tooltip formatter={(v) => [v, 'Resgates']} />
            <Bar dataKey="count" fill="#1B6CA8" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Pie chart */}
      {data.byOffer.length > 0 && (
        <div className="rounded-2xl border border-edge bg-surface p-5 shadow-sm">
          <h2 className="mb-4 font-bold text-fg">Resgates por oferta</h2>
          <div className="flex flex-col items-center gap-6 sm:flex-row">
            <ResponsiveContainer width={200} height={200}>
              <PieChart>
                <Pie
                  data={data.byOffer}
                  dataKey="count"
                  nameKey="title"
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                >
                  {data.byOffer.map((_, i) => (
                    <Cell key={i} fill={COLORS[i % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(v, name) => [v, name]} />
              </PieChart>
            </ResponsiveContainer>
            <div className="flex-1">
              {data.byOffer.map((o, i) => (
                <div key={o.offerId} className="flex items-center justify-between py-1.5 border-b border-edge last:border-0">
                  <div className="flex items-center gap-2">
                    <span className="h-3 w-3 rounded-full" style={{ backgroundColor: COLORS[i % COLORS.length] }} />
                    <span className="text-sm text-fg">{o.title}</span>
                  </div>
                  <span className="text-sm font-bold text-fg">{o.count}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
